<template>
  <div class="kps-dashboard">
    <pre-loading v-if="!data" />
    <template v-else>
      <!-- Özet kartları -->
      <b-row class="match-height">
        <b-col
          v-for="card in summaryCards"
          :key="card.title"
          cols="12"
          sm="6"
          xl="3"
        >
          <b-card
            no-body
            class="stat-card"
          >
            <b-card-body class="d-flex align-items-center">
              <b-avatar
                :variant="card.variant"
                size="48"
                class="mr-1"
              >
                <feather-icon
                  :icon="card.icon"
                  size="22"
                />
              </b-avatar>
              <div class="flex-grow-1">
                <h3 class="mb-0 font-weight-bolder">
                  {{ card.value }}
                  <b-badge
                    v-if="card.badge"
                    pill
                    :variant="`light-${card.variant}`"
                    class="ml-50 align-middle font-small-1"
                  >
                    {{ card.badge }}
                  </b-badge>
                </h3>
                <div class="text-muted font-small-3">
                  {{ card.title }}
                </div>
              </div>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>

      <b-row class="match-height">
        <!-- Aylık form grafiği -->
        <b-col
          cols="12"
          lg="8"
        >
          <b-card>
            <div class="d-flex justify-content-between align-items-start mb-1">
              <div>
                <b-card-title class="mb-25">
                  Form Başvuruları
                </b-card-title>
                <div class="text-muted font-small-3">
                  Son 12 ay, aylık
                </div>
              </div>
              <div class="text-right font-small-3">
                <div>
                  <span class="legend-dot bg-primary" /> İletişim: <b>{{ forms.webforms.total }}</b>
                </div>
                <div>
                  <span class="legend-dot bg-dark" /> Kariyer: <b>{{ forms.hr_form.total }}</b>
                </div>
              </div>
            </div>
            <vue-apex-charts
              type="bar"
              height="280"
              :options="chartOptions"
              :series="chartSeries"
            />
          </b-card>
        </b-col>

        <!-- Çeviri durumu -->
        <b-col
          cols="12"
          lg="4"
        >
          <b-card>
            <b-card-title class="mb-25">
              Çeviri Durumu
            </b-card-title>
            <div class="text-muted font-small-3 mb-1">
              Eksik EN / DE çevirisi olan içerikler
            </div>
            <div
              v-for="row in data.translations"
              :key="row.component"
              class="translation-row"
            >
              <div class="d-flex justify-content-between font-small-3">
                <span class="font-weight-bold">{{ row.title }}</span>
                <span>
                  <b-link
                    v-for="lang in ['en', 'de']"
                    :key="lang"
                    :to="row.form_id ? `/app/translate/data-list/${lang}/${row.form_id}` : null"
                    :class="row[lang] < row.total ? 'text-danger' : 'text-success'"
                    class="ml-50"
                  >
                    {{ lang.toUpperCase() }} {{ row[lang] }}/{{ row.total }}
                  </b-link>
                </span>
              </div>
              <b-progress
                :max="row.total * 2 || 1"
                height="5px"
                class="mt-25"
              >
                <b-progress-bar
                  :value="row.en + row.de"
                  :variant="row.en + row.de < row.total * 2 ? 'warning' : 'success'"
                />
              </b-progress>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <!-- Son başvurular -->
      <b-row class="match-height">
        <b-col
          v-for="table in latestTables"
          :key="table.title"
          cols="12"
          lg="6"
        >
          <b-card no-body>
            <b-card-header>
              <b-card-title>{{ table.title }}</b-card-title>
              <b-button
                variant="flat-primary"
                size="sm"
                :to="table.listUrl"
              >
                Tümü
                <feather-icon icon="ChevronRightIcon" />
              </b-button>
            </b-card-header>
            <b-table
              :items="table.items"
              :fields="table.fields"
              responsive
              show-empty
              :empty-text="table.empty"
              class="mb-0"
              hover
              @row-clicked="item => $router.push(table.viewUrl + item.id)"
            >
              <template #cell(name)="row">
                <div class="font-weight-bold">
                  {{ row.item.name }}
                </div>
                <div class="text-muted font-small-2">
                  {{ row.item.email }}
                </div>
              </template>
              <template #cell(subject)="row">
                <span class="text-truncate d-inline-block subject-cell">{{ row.item.subject || '-' }}</span>
              </template>
              <template #cell(filename)="row">
                <feather-icon
                  :icon="row.item.filename ? 'PaperclipIcon' : 'MinusIcon'"
                  :class="row.item.filename ? 'text-success' : 'text-muted'"
                />
              </template>
              <template #cell(created)="row">
                <span class="font-small-3 text-nowrap">{{ fromNow(row.item.created) }}</span>
              </template>
            </b-table>
          </b-card>
        </b-col>
      </b-row>

      <b-row class="match-height">
        <!-- İçerik sayıları -->
        <b-col
          cols="12"
          lg="8"
        >
          <b-card>
            <b-card-title>Site İçeriği</b-card-title>
            <b-row>
              <b-col
                v-for="item in data.content"
                :key="item.key"
                cols="6"
                md="4"
              >
                <b-link
                  :to="contentLinks[item.key]"
                  class="content-tile"
                >
                  <h2 class="mb-0 text-primary font-weight-bolder">
                    {{ item.total }}
                  </h2>
                  <div class="text-body font-small-3">
                    {{ item.title }}
                  </div>
                </b-link>
              </b-col>
            </b-row>
          </b-card>
        </b-col>

        <!-- Son haberler -->
        <b-col
          cols="12"
          lg="4"
        >
          <b-card>
            <div class="d-flex justify-content-between align-items-center mb-1">
              <b-card-title class="mb-0">
                Son Haberler
              </b-card-title>
              <b-button
                variant="primary"
                size="sm"
                to="/app/blog/add"
              >
                <feather-icon icon="PlusIcon" />
                Haber Ekle
              </b-button>
            </div>
            <div
              v-if="!data.news.length"
              class="text-muted"
            >
              Henüz haber yok.
            </div>
            <b-link
              v-for="post in data.news"
              :key="post.id"
              :to="`/app/blog/edit/${post.id}`"
              class="news-row d-block"
            >
              <div class="text-body font-weight-bold text-truncate">
                {{ post.title }}
              </div>
              <div class="text-muted font-small-2">
                {{ formatDate(post.created) }}
              </div>
            </b-link>
          </b-card>
        </b-col>
      </b-row>
    </template>
  </div>
</template>

<script>
import {
  BRow, BCol, BCard, BCardBody, BCardHeader, BCardTitle, BAvatar, BBadge, BTable, BButton, BLink, BProgress,
  BProgressBar,
} from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'
import PreLoading from '@/layouts/components/common/PreLoading.vue'

const KPS_RED = '#bc1e2c'
const KPS_DARK = '#2b2b2b'

export default {
  name: 'Manager',
  components: {
    BRow,
    BCol,
    BCard,
    BCardBody,
    BCardHeader,
    BCardTitle,
    BAvatar,
    BBadge,
    BTable,
    BButton,
    BLink,
    BProgress,
    BProgressBar,
    VueApexCharts,
    PreLoading,
  },
  data() {
    return {
      contentLinks: {
        production: '/app/production',
        product_categories: '/app/product-groups',
        blog: '/app/blog',
        references: '/app/references',
        certificates: '/app/certificates',
        contents: '/app/contents',
      },
    }
  },
  computed: {
    data() {
      return this.$store.getters['dashboard/data']
    },
    forms() {
      return this.data.forms
    },
    summaryCards() {
      const { webforms, hr_form: hr } = this.forms
      return [
        {
          title: 'İletişim formu (30 gün)', value: webforms.last30, badge: `Bugün ${webforms.today}`, icon: 'MailIcon', variant: 'primary',
        },
        {
          title: 'Kariyer başvurusu (30 gün)', value: hr.last30, badge: `Bugün ${hr.today}`, icon: 'BriefcaseIcon', variant: 'dark',
        },
        {
          title: 'Son 7 gün toplam form', value: webforms.last7 + hr.last7, icon: 'TrendingUpIcon', variant: 'success',
        },
        {
          title: 'Eksik çeviri', value: this.missingTranslations, icon: 'FlagIcon', variant: this.missingTranslations ? 'warning' : 'success',
        },
      ]
    },
    missingTranslations() {
      return this.data.translations.reduce((sum, row) => sum + (row.total - row.en) + (row.total - row.de), 0)
    },
    chartSeries() {
      return [
        { name: 'İletişim Formu', data: this.data.monthly.map(m => m.webforms) },
        { name: 'Kariyer Başvurusu', data: this.data.monthly.map(m => m.hr_form) },
      ]
    },
    chartOptions() {
      return {
        chart: {
          stacked: true,
          toolbar: { show: false },
          fontFamily: 'inherit',
        },
        colors: [KPS_RED, KPS_DARK],
        plotOptions: {
          bar: { columnWidth: '45%', borderRadius: 3 },
        },
        dataLabels: { enabled: false },
        legend: { show: false },
        grid: {
          borderColor: '#ebe9f1',
          padding: { left: 0, right: 0 },
        },
        xaxis: {
          categories: this.data.monthly.map(m => this.moment(`${m.month}-01`).format('MMM YY')),
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: {
          min: 0,
          forceNiceScale: true,
          labels: { formatter: val => Math.round(val) },
        },
        tooltip: { shared: true, intersect: false },
      }
    },
    latestTables() {
      return [
        {
          title: 'Son İletişim Formları',
          items: this.data.latest.webforms,
          fields: [
            { key: 'name', label: 'Gönderen' },
            { key: 'subject', label: 'Konu' },
            { key: 'created', label: 'Tarih', thClass: 'text-nowrap' },
          ],
          empty: 'Henüz form gelmedi.',
          listUrl: '/app/webforms',
          viewUrl: '/app/webforms/view/',
        },
        {
          title: 'Son Kariyer Başvuruları',
          items: this.data.latest.hr_form,
          fields: [
            { key: 'name', label: 'Aday' },
            { key: 'filename', label: 'CV', thStyle: { width: '60px' } },
            { key: 'created', label: 'Tarih', thClass: 'text-nowrap' },
          ],
          empty: 'Henüz başvuru yok.',
          listUrl: '/app/hr-forms',
          viewUrl: '/app/hr-forms/view/',
        },
      ]
    },
  },
  created() {
    this.$store.dispatch('dashboard/getData')
  },
  methods: {
    fromNow(value) {
      return value ? this.moment(value).fromNow() : '-'
    },
    formatDate(value) {
      return value ? this.moment(value).format('DD.MM.YYYY') : '-'
    },
  },
}
</script>

<style lang="scss" scoped>
.stat-card .card-body {
  padding: 1.25rem;
}
.legend-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 4px;
}
.translation-row + .translation-row {
  margin-top: 0.85rem;
}
.subject-cell {
  max-width: 220px;
}
.content-tile {
  display: block;
  padding: 1rem;
  margin-bottom: 1rem;
  border: 1px solid #ebe9f1;
  border-radius: 6px;
  transition: border-color 0.2s, box-shadow 0.2s;
  &:hover {
    border-color: #bc1e2c;
    box-shadow: 0 4px 18px rgba(188, 30, 44, 0.12);
  }
}
.news-row {
  padding: 0.6rem 0;
  border-top: 1px solid #ebe9f1;
  &:first-of-type {
    border-top: 0;
  }
}
::v-deep .table tbody tr {
  cursor: pointer;
}
</style>
